import { QueryClient } from '@tanstack/react-query'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'

import { DistrictFilter } from '@/features/district-filter/components/district-filter'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { type DistrictsResponse, getAllDistricts } from '@/features/district-filter/api/get-districts'
import { getDefaultMeta } from '@/shared/utils'
import ContextEnhancer from '@/shared/libs/context-enhancer'
import FlexRow from '@/components/shared/FlexRow'
import FlexCol from '@/components/shared/FlexCol'
import { DistrictFilterTargetEntityNames } from '@/features/district-filter/district-filter.types'
import { GlobalPageProps } from '@/pages/_app'

interface DistrictFilterPageProps extends GlobalPageProps {
  data: {
    district: DistrictsResponse
  }
  query: {
    targetEntityName: DistrictFilterTargetEntityNames
  }
}

const DistrictFilterPage = ({ data: { district }, query: { targetEntityName } }: DistrictFilterPageProps) => {
  return (
    <ResponsiveLayout>
      <TopDownloadScreen useFooter={false} isTopDownloadBannerHidden>
        <FlexCol className="h-[calc(var(--vh-unit)*100-48px)]">
          <DistrictFilter district={district} targetEntityName={targetEntityName}>
            <DistrictFilter.Header />
            <FlexRow className="flex-1 overflow-y-auto">
              <DistrictFilter.CityList />
              <DistrictFilter.DistrictList />
            </FlexRow>
          </DistrictFilter>
        </FlexCol>
      </TopDownloadScreen>
    </ResponsiveLayout>
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<DistrictFilterPageProps>> => {
  const contextEnhancer = new ContextEnhancer(ctx)
  const { target } = ctx.query
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
  try {
    const queryClient = new QueryClient()
    await queryClient.prefetchQuery({
      queryKey: ['districts'],
      queryFn: async () => {
        const district: DistrictsResponse = await getAllDistricts({
          Authorization: enhancedContext.token,
          'Accept-Language': enhancedContext.locale,
        })
        return district
      },
    })

    ctx.res.setHeader('Cache-Control', 'public, max-age=3600, stale-while-revalidate=3600')
    ctx.res.setHeader('x-robots-tag', 'noindex, follow')

    return {
      props: {
        data: {
          district: queryClient.getQueryData(['districts']) as DistrictsResponse,
        },
        query: {
          targetEntityName: (target as DistrictFilterTargetEntityNames) || DistrictFilterTargetEntityNames.EVENTS,
        },
        user: enhancedContext.user,
        meta: getDefaultMeta(enhancedContext.locale),
      },
    }
  } catch (error) {
    console.error('getServerSideProps error:', error)
    return {
      redirect: {
        destination: ctx.req.headers.referer || `${enhancedContext.pathnamePrefix}/`,
        permanent: false,
      },
    }
  }
}

export default DistrictFilterPage
